/**
 * MEIA Studio — Backend-Persistence Enum Definitions
 *
 * Enums that exist because the Azymmetric backend persists MEIA studies to
 * Postgres. Not part of the MEIA canonical schema proper — these are the
 * persistence-layer/polymorphic discriminators the API needs to model
 * single-table inheritance, study lifecycle, and reporting.
 *
 * GOVERNANCE: Shared is the single source of truth. Import these enums
 * directly from `@azymmetric/shared/types/meia/...` anywhere they are
 * needed. Do not add backend passthrough wrappers for them.
 *
 * All enums use SCREAMING_SNAKE values per the Azymmetric Postgres-enum
 * convention; values are alphabetized.
 */
// ─────────────────────────────────────────────────────────────
// Study Lifecycle
// ─────────────────────────────────────────────────────────────
/** Study-root adversary profile tags used for P1 mission framing. */
export var StudyAdversaryProfile;
(function (StudyAdversaryProfile) {
    StudyAdversaryProfile["CYBER_ACTOR"] = "CYBER_ACTOR";
    StudyAdversaryProfile["HYBRID_THREAT"] = "HYBRID_THREAT";
    StudyAdversaryProfile["NEAR_PEER_EUROPEAN"] = "NEAR_PEER_EUROPEAN";
    StudyAdversaryProfile["NEAR_PEER_PACIFIC"] = "NEAR_PEER_PACIFIC";
    StudyAdversaryProfile["NON_STATE_ACTOR"] = "NON_STATE_ACTOR";
    StudyAdversaryProfile["PROXY_FORCE"] = "PROXY_FORCE";
    StudyAdversaryProfile["REGIONAL_POWER"] = "REGIONAL_POWER";
    StudyAdversaryProfile["UNATTRIBUTED"] = "UNATTRIBUTED";
})(StudyAdversaryProfile || (StudyAdversaryProfile = {}));
/** Mission-framing capability-gap priority authored under a mission. */
export var StudyMissionCapabilityGapPriority;
(function (StudyMissionCapabilityGapPriority) {
    StudyMissionCapabilityGapPriority["HIGH"] = "HIGH";
    StudyMissionCapabilityGapPriority["LOW"] = "LOW";
    StudyMissionCapabilityGapPriority["MEDIUM"] = "MEDIUM";
})(StudyMissionCapabilityGapPriority || (StudyMissionCapabilityGapPriority = {}));
/** Mission-framing capability-gap lifecycle authored under a mission. */
export var StudyMissionCapabilityGapStatus;
(function (StudyMissionCapabilityGapStatus) {
    StudyMissionCapabilityGapStatus["ANSWERED"] = "ANSWERED";
    StudyMissionCapabilityGapStatus["INVESTIGATING"] = "INVESTIGATING";
    StudyMissionCapabilityGapStatus["OPEN"] = "OPEN";
})(StudyMissionCapabilityGapStatus || (StudyMissionCapabilityGapStatus = {}));
/** Confidence tier attached to red-force provenance metadata. */
export var ConfidenceTier;
(function (ConfidenceTier) {
    ConfidenceTier["A"] = "A";
    ConfidenceTier["B"] = "B";
    ConfidenceTier["C"] = "C";
    ConfidenceTier["D"] = "D";
    ConfidenceTier["F"] = "F";
})(ConfidenceTier || (ConfidenceTier = {}));
/** Mechanism vocabulary for target engagements, distinct from doctrinal Effect. */
export var EngagementType;
(function (EngagementType) {
    EngagementType["CYBER"] = "cyber";
    EngagementType["DECEPTION"] = "deception";
    EngagementType["GUIDANCE"] = "guidance";
    EngagementType["JAM"] = "jam";
    EngagementType["KINETIC"] = "kinetic";
    EngagementType["NONE"] = "none";
    EngagementType["SOFTKILL"] = "softkill";
    EngagementType["TERMINAL"] = "terminal";
})(EngagementType || (EngagementType = {}));
/** Decision-maker override vocabulary for Effect-on-Target adjudication. */
export var EffectOnTargetStatus;
(function (EffectOnTargetStatus) {
    EffectOnTargetStatus["CONTESTED"] = "CONTESTED";
    EffectOnTargetStatus["DEGRADED"] = "DEGRADED";
    EffectOnTargetStatus["SUPPRESSED"] = "SUPPRESSED";
    EffectOnTargetStatus["UNENGAGED"] = "UNENGAGED";
})(EffectOnTargetStatus || (EffectOnTargetStatus = {}));
/** Prototype-aligned source vocabulary for red-force activity provenance. */
export var RedForceActivityProvenanceSource;
(function (RedForceActivityProvenanceSource) {
    RedForceActivityProvenanceSource["ADMIN"] = "admin";
    RedForceActivityProvenanceSource["INTEL_PACKAGE"] = "intel-pkg";
    RedForceActivityProvenanceSource["JLLIS"] = "JLLIS";
    RedForceActivityProvenanceSource["SME"] = "sme";
    RedForceActivityProvenanceSource["USER"] = "user";
})(RedForceActivityProvenanceSource || (RedForceActivityProvenanceSource = {}));
/** Mission-level lifecycle for MEIA Mission Framing. */
export var StudyMissionStatus;
(function (StudyMissionStatus) {
    StudyMissionStatus["ACTIVE"] = "ACTIVE";
    StudyMissionStatus["CLOSED"] = "CLOSED";
    StudyMissionStatus["DRAFT"] = "DRAFT";
    StudyMissionStatus["UNDER_REVIEW"] = "UNDER_REVIEW";
})(StudyMissionStatus || (StudyMissionStatus = {}));
/** Top-level study lifecycle state. */
export var StudyStatus;
(function (StudyStatus) {
    StudyStatus["ARCHIVED"] = "ARCHIVED";
    StudyStatus["APPROVED"] = "APPROVED";
    StudyStatus["DRAFT"] = "DRAFT";
    StudyStatus["PROPOSED"] = "PROPOSED";
})(StudyStatus || (StudyStatus = {}));
/** Study-root planning time horizon used for P1 mission framing. */
export var StudyTimeHorizon;
(function (StudyTimeHorizon) {
    StudyTimeHorizon["FAR_TERM_FY36_PLUS"] = "FAR_TERM_FY36_PLUS";
    StudyTimeHorizon["FYDP_FY28_30"] = "FYDP_FY28_30";
    StudyTimeHorizon["MID_TERM_FY31_35"] = "MID_TERM_FY31_35";
    StudyTimeHorizon["NEAR_TERM_FY26_27"] = "NEAR_TERM_FY26_27";
})(StudyTimeHorizon || (StudyTimeHorizon = {}));
/** Authoring gates used for stage sign-off (BV-047/048 escalate severity at G4). */
export var StudyGate;
(function (StudyGate) {
    StudyGate["G1"] = "G1";
    StudyGate["G2"] = "G2";
    StudyGate["G3"] = "G3";
    StudyGate["G4"] = "G4";
})(StudyGate || (StudyGate = {}));
/**
 * Per-study stakeholder role persisted on `StudyStakeholder.role` (Study Role / Axis 2).
 * Assignable roles are Decision Maker of Record, Reviewer, and Sponsor; `OWNER` is the
 * derived Author/Lead (from `Study.ownerId`). See ASSIGNABLE_STUDY_STAKEHOLDER_ROLES.
 */
export var StudyStakeholderRole;
(function (StudyStakeholderRole) {
    StudyStakeholderRole["DECISION_MAKER"] = "DECISION_MAKER";
    /** @deprecated AZ-451: Mission Engineer is now an Intelligence Persona (Axis 1), not a study role. Kept inert for back-compat; never assigned. */
    StudyStakeholderRole["MISSION_ENGINEER"] = "MISSION_ENGINEER";
    StudyStakeholderRole["OWNER"] = "OWNER";
    StudyStakeholderRole["REVIEWER"] = "REVIEWER";
    StudyStakeholderRole["SPONSOR"] = "SPONSOR";
})(StudyStakeholderRole || (StudyStakeholderRole = {}));
/** Top-level campaign lifecycle state. */
export var CampaignStatus;
(function (CampaignStatus) {
    CampaignStatus["ACTIVE"] = "ACTIVE";
    CampaignStatus["ARCHIVED"] = "ARCHIVED";
    CampaignStatus["DRAFT"] = "DRAFT";
    CampaignStatus["HISTORICAL"] = "HISTORICAL";
})(CampaignStatus || (CampaignStatus = {}));
/** Campaign/scenario/vignette level of warfare. */
export var CampaignLevelOfWarfare;
(function (CampaignLevelOfWarfare) {
    CampaignLevelOfWarfare["OPERATIONAL"] = "OPERATIONAL";
    CampaignLevelOfWarfare["STRATEGIC"] = "STRATEGIC";
    CampaignLevelOfWarfare["TACTICAL"] = "TACTICAL";
})(CampaignLevelOfWarfare || (CampaignLevelOfWarfare = {}));
/** Lead command authoring/owning a campaign. */
export var LeadCommand;
(function (LeadCommand) {
    LeadCommand["ACC"] = "ACC";
    LeadCommand["AFCYBER"] = "AFCYBER";
    LeadCommand["AFGSC"] = "AFGSC";
    LeadCommand["AFRICOM"] = "AFRICOM";
    LeadCommand["AFRL"] = "AFRL";
    LeadCommand["AU_HQJOC"] = "AU_HQJOC";
    LeadCommand["CA_CJOC"] = "CA_CJOC";
    LeadCommand["CENTCOM"] = "CENTCOM";
    LeadCommand["CYBERCOM"] = "CYBERCOM";
    LeadCommand["EUCOM"] = "EUCOM";
    LeadCommand["INDOPACOM"] = "INDOPACOM";
    LeadCommand["JP_JSO"] = "JP_JSO";
    LeadCommand["KR_JCS"] = "KR_JCS";
    LeadCommand["NATO_ACO"] = "NATO_ACO";
    LeadCommand["NATO_SHAPE"] = "NATO_SHAPE";
    LeadCommand["NORTHCOM"] = "NORTHCOM";
    LeadCommand["NZ_HQJFNZ"] = "NZ_HQJFNZ";
    LeadCommand["PACAF"] = "PACAF";
    LeadCommand["SOCOM"] = "SOCOM";
    LeadCommand["SOUTHCOM"] = "SOUTHCOM";
    LeadCommand["SPACECOM"] = "SPACECOM";
    LeadCommand["STRATCOM"] = "STRATCOM";
    LeadCommand["TRANSCOM"] = "TRANSCOM";
    LeadCommand["UK_PJHQ"] = "UK_PJHQ";
    LeadCommand["USAFE"] = "USAFE";
})(LeadCommand || (LeadCommand = {}));
export const LEAD_COMMAND_LABELS = {
    [LeadCommand.ACC]: 'ACC',
    [LeadCommand.AFCYBER]: 'AFCYBER',
    [LeadCommand.AFGSC]: 'AFGSC',
    [LeadCommand.AFRICOM]: 'AFRICOM',
    [LeadCommand.AFRL]: 'AFRL',
    [LeadCommand.AU_HQJOC]: 'AU HQJOC',
    [LeadCommand.CA_CJOC]: 'CA CJOC',
    [LeadCommand.CENTCOM]: 'CENTCOM',
    [LeadCommand.CYBERCOM]: 'CYBERCOM',
    [LeadCommand.EUCOM]: 'EUCOM',
    [LeadCommand.INDOPACOM]: 'INDOPACOM',
    [LeadCommand.JP_JSO]: 'JP JSO',
    [LeadCommand.KR_JCS]: 'KR JCS',
    [LeadCommand.NATO_ACO]: 'NATO ACO',
    [LeadCommand.NATO_SHAPE]: 'NATO SHAPE',
    [LeadCommand.NORTHCOM]: 'NORTHCOM',
    [LeadCommand.NZ_HQJFNZ]: 'NZ HQJFNZ',
    [LeadCommand.PACAF]: 'PACAF',
    [LeadCommand.SOCOM]: 'SOCOM',
    [LeadCommand.SOUTHCOM]: 'SOUTHCOM',
    [LeadCommand.SPACECOM]: 'SPACECOM',
    [LeadCommand.STRATCOM]: 'STRATCOM',
    [LeadCommand.TRANSCOM]: 'TRANSCOM',
    [LeadCommand.UK_PJHQ]: 'UK PJHQ',
    [LeadCommand.USAFE]: 'USAFE',
};
/** Coalition partner participating in a campaign. */
export var CoalitionPartner;
(function (CoalitionPartner) {
    CoalitionPartner["AU"] = "AU";
    CoalitionPartner["CA"] = "CA";
    CoalitionPartner["DE"] = "DE";
    CoalitionPartner["FR"] = "FR";
    CoalitionPartner["IT"] = "IT";
    CoalitionPartner["JP"] = "JP";
    CoalitionPartner["KR"] = "KR";
    CoalitionPartner["NATO"] = "NATO";
    CoalitionPartner["NL"] = "NL";
    CoalitionPartner["NO"] = "NO";
    CoalitionPartner["NZ"] = "NZ";
    CoalitionPartner["UK"] = "UK";
    CoalitionPartner["US"] = "US";
})(CoalitionPartner || (CoalitionPartner = {}));
export const COALITION_PARTNER_LABELS = {
    [CoalitionPartner.AU]: 'Australia',
    [CoalitionPartner.CA]: 'Canada',
    [CoalitionPartner.DE]: 'Germany',
    [CoalitionPartner.FR]: 'France',
    [CoalitionPartner.IT]: 'Italy',
    [CoalitionPartner.JP]: 'Japan',
    [CoalitionPartner.KR]: 'South Korea',
    [CoalitionPartner.NATO]: 'NATO',
    [CoalitionPartner.NL]: 'Netherlands',
    [CoalitionPartner.NO]: 'Norway',
    [CoalitionPartner.NZ]: 'New Zealand',
    [CoalitionPartner.UK]: 'United Kingdom',
    [CoalitionPartner.US]: 'United States',
};
/** Operational region used by scenarios and vignette overrides. */
export var OperationalRegion;
(function (OperationalRegion) {
    OperationalRegion["ARCTIC"] = "ARCTIC";
    OperationalRegion["BALTIC"] = "BALTIC";
    OperationalRegion["BLACK_SEA"] = "BLACK_SEA";
    OperationalRegion["CARIBBEAN"] = "CARIBBEAN";
    OperationalRegion["CENTRAL_EUROPE"] = "CENTRAL_EUROPE";
    OperationalRegion["CONUS"] = "CONUS";
    OperationalRegion["EAS"] = "EAS";
    OperationalRegion["GLOBAL"] = "GLOBAL";
    OperationalRegion["GULF_STATES"] = "GULF_STATES";
    OperationalRegion["HORN_AFRICA"] = "HORN_AFRICA";
    OperationalRegion["INDOPACOM_NORTH"] = "INDOPACOM_NORTH";
    OperationalRegion["INDOPACOM_SOUTH"] = "INDOPACOM_SOUTH";
    OperationalRegion["LEVANT"] = "LEVANT";
    OperationalRegion["NORTH_ATLANTIC"] = "NORTH_ATLANTIC";
    OperationalRegion["SCS"] = "SCS";
})(OperationalRegion || (OperationalRegion = {}));
export const OPERATIONAL_REGION_LABELS = {
    [OperationalRegion.ARCTIC]: 'Arctic',
    [OperationalRegion.BALTIC]: 'Baltic',
    [OperationalRegion.BLACK_SEA]: 'Black Sea',
    [OperationalRegion.CARIBBEAN]: 'Caribbean',
    [OperationalRegion.CENTRAL_EUROPE]: 'Central Europe',
    [OperationalRegion.CONUS]: 'CONUS',
    [OperationalRegion.EAS]: 'East Asian Seas',
    [OperationalRegion.GLOBAL]: 'Global',
    [OperationalRegion.GULF_STATES]: 'Gulf States',
    [OperationalRegion.HORN_AFRICA]: 'Horn of Africa',
    [OperationalRegion.INDOPACOM_NORTH]: 'INDOPACOM North',
    [OperationalRegion.INDOPACOM_SOUTH]: 'INDOPACOM South',
    [OperationalRegion.LEVANT]: 'Levant',
    [OperationalRegion.NORTH_ATLANTIC]: 'North Atlantic',
    [OperationalRegion.SCS]: 'South China Sea',
};
// ─────────────────────────────────────────────────────────────
// Scoring Runs & Reports
// ─────────────────────────────────────────────────────────────
/** Scoring-run execution state persisted on `StudyScoringRun`. */
export var ScoringRunStatus;
(function (ScoringRunStatus) {
    ScoringRunStatus["COMPLETE"] = "COMPLETE";
    ScoringRunStatus["FAILED"] = "FAILED";
    ScoringRunStatus["PENDING"] = "PENDING";
    ScoringRunStatus["RUNNING"] = "RUNNING";
    ScoringRunStatus["STALE"] = "STALE";
})(ScoringRunStatus || (ScoringRunStatus = {}));
/** Export format persisted on `StudyReport`. */
export var StudyReportFormat;
(function (StudyReportFormat) {
    StudyReportFormat["CSV"] = "CSV";
    StudyReportFormat["JSON"] = "JSON";
    StudyReportFormat["PDF"] = "PDF";
})(StudyReportFormat || (StudyReportFormat = {}));
/**
 * Thread role persisted on `StudyThread.role`. Drives E2E-view spine colours
 * and downstream verdict aggregation. A single mission can mix roles across
 * threads (e.g., F2T2EA can be either PRIMARY or SHAPING depending on the
 * thread's narrative purpose), so role is authored, not derived.
 */
export var StudyThreadRole;
(function (StudyThreadRole) {
    StudyThreadRole["PRIMARY"] = "PRIMARY";
    StudyThreadRole["SHAPING"] = "SHAPING";
    StudyThreadRole["SUPPORTING"] = "SUPPORTING";
})(StudyThreadRole || (StudyThreadRole = {}));
/** DOTMLPF-P dimension persisted on `StudyArchitectureDotmlpf.dimension`. */
export var DotmlpfDimension;
(function (DotmlpfDimension) {
    DotmlpfDimension["DOCTRINE"] = "DOCTRINE";
    DotmlpfDimension["FACILITIES"] = "FACILITIES";
    DotmlpfDimension["LEADERSHIP"] = "LEADERSHIP";
    DotmlpfDimension["MATERIEL"] = "MATERIEL";
    DotmlpfDimension["ORGANIZATION"] = "ORGANIZATION";
    DotmlpfDimension["PERSONNEL"] = "PERSONNEL";
    DotmlpfDimension["POLICY"] = "POLICY";
    DotmlpfDimension["TRAINING"] = "TRAINING";
})(DotmlpfDimension || (DotmlpfDimension = {}));
// ─────────────────────────────────────────────────────────────
// Polymorphic discriminators
// ─────────────────────────────────────────────────────────────
/**
 * Node-kind discriminator persisted on `StudyExplainabilityTrace.nodeType`.
 * Widens the MEIA `TraceStep.entityType` set to include TECHNOLOGY and
 * ENGAGEMENT — the Azymmetric trace tree carries component-scope richer
 * bindings than the canonical MEIA trace.
 */
export var ExplainabilityNodeType;
(function (ExplainabilityNodeType) {
    ExplainabilityNodeType["ENGAGEMENT"] = "ENGAGEMENT";
    ExplainabilityNodeType["MISSION"] = "MISSION";
    ExplainabilityNodeType["MOE"] = "MOE";
    ExplainabilityNodeType["MOP"] = "MOP";
    ExplainabilityNodeType["MOS"] = "MOS";
    ExplainabilityNodeType["TASK"] = "TASK";
    ExplainabilityNodeType["TECHNOLOGY"] = "TECHNOLOGY";
})(ExplainabilityNodeType || (ExplainabilityNodeType = {}));
/**
 * Target-kind discriminator persisted on `StudyAcrEntry.linkedEntityType`.
 * Superset of MEIA's `linkedThreads / linkedTasks / linkedTechnologies`
 * trio — a single column discriminates across eleven link kinds.
 */
export var StudyLinkableEntityType;
(function (StudyLinkableEntityType) {
    StudyLinkableEntityType["ARCHITECTURE_ALTERNATIVE"] = "ARCHITECTURE_ALTERNATIVE";
    StudyLinkableEntityType["MISSION"] = "MISSION";
    StudyLinkableEntityType["MOE"] = "MOE";
    StudyLinkableEntityType["MOP"] = "MOP";
    StudyLinkableEntityType["MOS"] = "MOS";
    StudyLinkableEntityType["RUN_CASE"] = "RUN_CASE";
    StudyLinkableEntityType["SWIMLANE"] = "SWIMLANE";
    StudyLinkableEntityType["TASK"] = "TASK";
    StudyLinkableEntityType["TECHNOLOGY"] = "TECHNOLOGY";
    StudyLinkableEntityType["THREAD"] = "THREAD";
    StudyLinkableEntityType["THREAT_TARGET"] = "THREAT_TARGET";
})(StudyLinkableEntityType || (StudyLinkableEntityType = {}));
/**
 * Target-kind discriminator persisted on `StudyOverride.targetType`.
 * Five scoring-hierarchy levels at which an analyst HITL override can land.
 */
export var StudyOverrideTargetType;
(function (StudyOverrideTargetType) {
    StudyOverrideTargetType["MISSION"] = "MISSION";
    StudyOverrideTargetType["MOE"] = "MOE";
    StudyOverrideTargetType["MOP"] = "MOP";
    StudyOverrideTargetType["MOS"] = "MOS";
    StudyOverrideTargetType["TASK"] = "TASK";
})(StudyOverrideTargetType || (StudyOverrideTargetType = {}));
